export function TermsPage() {
  return (
    <div className="min-h-screen bg-gray-950 text-gray-200">
      <div className="mx-auto max-w-3xl px-6 py-16">
        <a
          href="/"
          className="mb-8 inline-flex items-center gap-2 text-sm text-brand-400 hover:text-brand-300"
        >
          &larr; Back to OpenQuote Pro
        </a>

        <h1 className="mb-2 text-3xl font-bold text-white">Terms of Service</h1>
        <p className="mb-10 text-sm text-gray-400">Last updated: March 1, 2026</p>

        <div className="space-y-8 text-[15px] leading-relaxed text-gray-300">
          <section>
            <h2 className="mb-3 text-lg font-semibold text-white">1. Acceptance of Terms</h2>
            <p>
              By creating an account or using OpenQuote Pro (&ldquo;the Service&rdquo;), you agree
              to be bound by these Terms of Service (&ldquo;Terms&rdquo;). The Service is operated
              by OpenWare (&ldquo;we,&rdquo; &ldquo;us,&rdquo; &ldquo;our&rdquo;). If you do not
              agree to these Terms, do not use the Service.
            </p>
          </section>

          <section>
            <h2 className="mb-3 text-lg font-semibold text-white">2. Description of Service</h2>
            <p>
              OpenQuote Pro is a multi-tenant SaaS platform for creating, managing, and sharing
              product quotes. The Service includes product catalog management, quote building with
              dynamic pricing, PDF generation, cross-tenant quote sharing, and public share links.
            </p>
          </section>

          <section>
            <h2 className="mb-3 text-lg font-semibold text-white">3. Accounts</h2>
            <p className="mb-3">
              You must provide accurate and complete information when creating an account. You are
              responsible for maintaining the security of your account credentials and for all
              activity that occurs under your account.
            </p>
            <p>
              Each account belongs to an organization (tenant). The account owner is responsible
              for managing team members and their access levels within the organization.
            </p>
          </section>

          <section>
            <h2 className="mb-3 text-lg font-semibold text-white">4. Your Data</h2>
            <p className="mb-3">
              You retain ownership of all data you enter into the Service, including product
              catalogs, pricing information, reseller records, and quotes (&ldquo;Your
              Data&rdquo;). We do not claim any intellectual property rights over Your Data.
            </p>
            <p>
              You grant us a limited license to store, process, and display Your Data solely for
              the purpose of providing the Service to you. This includes rendering quotes in PDF
              format, displaying shared quotes to intended recipients, and serving public share
              link pages.
            </p>
          </section>

          <section>
            <h2 className="mb-3 text-lg font-semibold text-white">5. Acceptable Use</h2>
            <p className="mb-3">You agree not to:</p>
            <p className="mb-1">&bull; Use the Service for any unlawful purpose</p>
            <p className="mb-1">
              &bull; Attempt to gain unauthorized access to other tenants&apos; data or accounts
            </p>
            <p className="mb-1">
              &bull; Interfere with or disrupt the Service or its underlying infrastructure
            </p>
            <p className="mb-1">
              &bull; Upload malicious content or files containing viruses or harmful code
            </p>
            <p>
              &bull; Use the Service to send unsolicited communications or spam through share links
            </p>
          </section>

          <section>
            <h2 className="mb-3 text-lg font-semibold text-white">6. SMS Communications</h2>
            <p className="mb-3">
              By providing your phone number, you consent to receive transactional SMS messages
              from OpenQuote Pro. These are limited to account verification codes, password reset
              codes, quote delivery notifications, and account security alerts. We will never send
              marketing or promotional messages via SMS.
            </p>
            <p className="mb-3">
              You can opt out of SMS messages at any time by replying STOP to any message or by
              removing your phone number from your profile settings. Reply HELP for assistance.
              Standard message and data rates may apply. Message frequency varies based on your
              account activity.
            </p>
            <p>
              SMS services are provided via Twilio. By using SMS features, you also agree to{' '}
              <a
                href="https://www.twilio.com/en-us/legal/tos"
                target="_blank"
                rel="noopener noreferrer"
                className="text-brand-400 hover:underline"
              >
                Twilio&apos;s Terms of Service
              </a>
              .
            </p>
          </section>

          <section>
            <h2 className="mb-3 text-lg font-semibold text-white">7. Sharing and Visibility</h2>
            <p className="mb-3">
              The Service allows you to share quotes with other platform users (cross-tenant
              sharing) and with external recipients via public share links. You are responsible for
              the content you share and for ensuring you have the right to share it.
            </p>
            <p>
              Public share links are accessible to anyone with the URL. We provide view and
              download tracking for your shared content, but we cannot guarantee that recipients
              will not further distribute shared content.
            </p>
          </section>

          <section>
            <h2 className="mb-3 text-lg font-semibold text-white">8. Service Availability</h2>
            <p>
              We strive to maintain high availability of the Service but do not guarantee
              uninterrupted or error-free operation. We may perform maintenance that temporarily
              affects availability. We will make reasonable efforts to provide advance notice of
              planned maintenance.
            </p>
          </section>

          <section>
            <h2 className="mb-3 text-lg font-semibold text-white">
              9. Limitation of Liability
            </h2>
            <p>
              To the maximum extent permitted by law, OpenWare shall not be liable for any
              indirect, incidental, special, consequential, or punitive damages, including loss of
              profits, data, or business opportunities, arising from your use of the Service. Our
              total liability for any claim related to the Service shall not exceed the amount you
              paid us in the twelve months preceding the claim.
            </p>
          </section>

          <section>
            <h2 className="mb-3 text-lg font-semibold text-white">
              10. Disclaimer of Warranties
            </h2>
            <p>
              The Service is provided &ldquo;as is&rdquo; and &ldquo;as available&rdquo; without
              warranties of any kind, whether express or implied, including but not limited to
              warranties of merchantability, fitness for a particular purpose, and
              non-infringement. We do not warrant that the Service will meet your specific
              requirements or that quotes generated through the Service will be free of errors.
            </p>
          </section>

          <section>
            <h2 className="mb-3 text-lg font-semibold text-white">11. Termination</h2>
            <p className="mb-3">
              You may close your account at any time by contacting us. We may suspend or terminate
              your access to the Service if you violate these Terms or engage in conduct that we
              determine is harmful to other users or to the Service.
            </p>
            <p>
              Upon termination, your right to use the Service ceases immediately. We will retain
              your data for 30 days after termination to allow for data export, after which it will
              be permanently deleted.
            </p>
          </section>

          <section>
            <h2 className="mb-3 text-lg font-semibold text-white">12. Changes to Terms</h2>
            <p>
              We may update these Terms from time to time. We will notify registered users of
              material changes via email or in-app notification at least 14 days before the changes
              take effect. Continued use of the Service after changes take effect constitutes
              acceptance of the updated Terms.
            </p>
          </section>

          <section>
            <h2 className="mb-3 text-lg font-semibold text-white">13. Governing Law</h2>
            <p>
              These Terms are governed by the laws of the State of Colorado, United States, without
              regard to conflict of law principles. Any disputes arising from these Terms or the
              Service shall be resolved in the courts located in Boulder County, Colorado.
            </p>
          </section>

          <section>
            <h2 className="mb-3 text-lg font-semibold text-white">14. Contact</h2>
            <p>
              If you have questions about these Terms, contact us at{' '}
              <a href="mailto:legal@openquote.pro" className="text-brand-400 hover:underline">
                legal@openquote.pro
              </a>
              .
            </p>
          </section>
        </div>

        <div className="mt-12 border-t border-gray-800 pt-6 text-center text-xs text-gray-500">
          &copy; {new Date().getFullYear()} OpenWare. All rights reserved.
        </div>
      </div>
    </div>
  );
}
